import { currentLocale, formatCurrency, formatNumber as localizedNumber } from 'i18n/format';
import { t as translate } from 'i18n';
/**
 * Units of a measure and how its numbers read in the active locale.
 *
 * The unit is the Cube member's `format` as the widget columns carry it:
 * `currency` is roubles, `percent` is a value ALREADY in percent (12.5 means
 * 12,5 %, never ×100), anything else — or no `format`, as in widgets stored
 * before it existed — is a plain number.
 */

const NBSP = ' ';




/** 'currency' | 'percent' | '' for a column (or a bare format string). */
export function unitOf(columnOrFormat) {
  const format = typeof columnOrFormat === 'string' ? columnOrFormat : columnOrFormat?.format;
  return format === 'currency' || format === 'percent' ? format : '';
}

/** «₽», «%» or '' — the axis title of a unit. */
export function unitSymbol(unit) {
  const value = unitOf(unit);
  if (value === 'currency') return new Intl.NumberFormat(currentLocale(), { style: 'currency', currency: 'RUB' }).formatToParts(0).find((part) => part.type === 'currency').value;
  return value === 'percent' ? '%' : '';
}

const withUnit = (text, unit) => {
  const symbol = unitSymbol(unit);
  return symbol ? `${text}${NBSP}${symbol}` : text;
};

// -0 would print as «-0».
const finite = (value) => (typeof value === 'number' && Number.isFinite(value) ? value + 0 : null);

// Up to two decimals. A value that rounds to zero is zero: Intl keeps the
// sign of -0.001 and would print «-0».
const plainNumber = (v) => localizedNumber(Math.abs(v) < 0.005 ? 0 : v, { maximumFractionDigits: 2 });

/** A value in full, as a table cell shows it: «1 234 567 ₽», «1 234,50 ₽», «12,5 %», «1 234,57»; «—» when not a number. */
export function formatNumber(value, unit) {
  const v = finite(value);
  if (v === null) return '—';
  const u = unitOf(unit);
  if (u === 'currency') {
    const cents = Math.round(v * 100) + 0; // -0.004 rounds to -0: «0 ₽», not «-0 ₽»
    return formatCurrency(cents === 0 ? 0 : v, 'RUB', { minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: cents % 100 === 0 ? 0 : 2 });
  }
  if (u === 'percent') return localizedNumber((Math.abs(v) < 0.005 ? 0 : v) / 100, { style: 'percent', maximumFractionDigits: 2 });
  return plainNumber(v);
}

/**
 * A value on a chart (tooltip, pie legend) or a KPI tile: roubles whole
 * («50 898 242 ₽», half away from zero), anything else as `formatNumber`.
 * Kopecks stay in the table.
 */
export function formatAmount(value, unit) {
  const v = finite(value);
  if (v === null || unitOf(unit) !== 'currency') return formatNumber(value, unit);
  const rounded = Math.sign(v) * Math.round(Math.abs(v)) + 0; // -0.4 is «0 ₽», not «-0 ₽»
  return formatCurrency(rounded, 'RUB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

const SCALES = () => ([
  [1e9, translate('assistant.unitsBn')],
  [1e6, translate('assistant.unitsM')],
  [1e3, translate('assistant.unitsK')],
]);

/**
 * An axis tick: compact above a thousand («1,2 млн ₽», «850 тыс. ₽»,
 * «12 тыс.»), whole roubles below it, percent as is («12,5 %»).
 */
export function formatTick(value, unit) {
  const v = finite(value);
  if (v === null) return '';
  const u = unitOf(unit);
  if (u === 'percent') return formatNumber(v, u);
  const abs = Math.abs(v);
  for (let i = 0; i < SCALES().length; i += 1) {
    const [size, word] = SCALES()[i];
    if (abs < size) continue;
    // 999 950 rounds to «1 000 тыс.» — say «1 млн» instead.
    const scaled = Math.round((abs / size) * 10) / 10;
    if (scaled >= 1000 && i > 0) {
      const [bigger, biggerWord] = SCALES()[i - 1];
      return withUnit(`${v < 0 ? '-' : ''}${localizedNumber(abs / bigger, { maximumFractionDigits: 1 })}${NBSP}${biggerWord}`, u);
    }
    return withUnit(`${v < 0 ? '-' : ''}${localizedNumber(scaled, { maximumFractionDigits: 1 })}${NBSP}${word}`, u);
  }
  return formatAmount(v, u);
}

/**
 * A share of the whole, 0–100: «42,1 %». A share that is neither none nor
 * all never reads as «0 %» or «100 %»: «<0,1 %», «>99,9 %».
 */
export function formatShare(percent) {
  if (percent > 0 && percent < 0.05) return `<${formatNumber(0.1, 'percent')}`;
  if (percent < 100 && percent >= 99.95) return `>${formatNumber(99.9, 'percent')}`;
  return formatNumber(Math.round(percent * 10) / 10, 'percent');
}
